import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { TFunction } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, CircleCheckBig, LoaderCircle } from "lucide-react";
import { ComponentProps, useEffect, useMemo, useState } from "react";

import { toast } from "sonner";
import { openTicket } from "@/api/support/openTicket.ts";
import { Textarea } from "@/components/ui/textarea.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { setTitle } from "@/hooks/useHeader.ts";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z.object({
    category: z.enum(["General", "Raffles", "Payments"]),
    subject: z.string().min(8, t("validation.subjectMinLength")),
    description: z.string().min(8, t("validation.descriptionMinLength")),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const CreateSupportTicket = ({
  className,
  ...props
}: ComponentProps<"div">) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  useEffect(() => {
    setTitle(t("support.create.title"));
  }, [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: "General",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    try {
      await openTicket(values);
      toast(t("support.create.success"), {
        duration: 7000,
        icon: <CircleCheckBig className="size-4 text-green-600" />,
      });
    } catch (e: unknown) {
      setError(t("common.somethingWentWrongTryLater"));
    }
    setLoading(false);
  };

  return (
    <div
      className={cn(
        "mx-auto flex max-w-md min-w-[45vw] flex-col gap-6",
        className,
      )}
      {...props}
    >
      <Card>
        <CardContent className="py-6">
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("support.create.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-6">
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="category">
                          {t("support.create.category")}
                        </FormLabel>
                        <FormControl>
                          <Select
                            {...field}
                            onValueChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                            defaultValue={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t("support.create.selectCategory")}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="General">
                                {t("support.create.categoryGeneral")}
                              </SelectItem>
                              <SelectItem value="Raffles">
                                {t("support.create.categoryRaffles")}
                              </SelectItem>
                              <SelectItem value="Payments">
                                {t("support.create.categoryPayments")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="subject">
                          {t("support.create.subject")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            id="subject"
                            type="string"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="description">
                          {t("support.create.description")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            id="description"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={loading}>
                    {t("support.create.submit")}{" "}
                    {loading && <LoaderCircle className="animate-spin" />}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateSupportTicket;
